import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/useAuth';
import type {
  AssignmentStatus,
  HrAssignmentProgressKpi,
  HrDocumentAssignment,
  HrDocumentAssignmentWithRelations,
  HrDocumentResponse,
  ResponseMap,
} from './types';

const SELECT_WITH_RELATIONS = `
  *,
  employee:employees!employee_id(id, first_name, last_name, email),
  document:hr_documents!document_id(id, title, description, version),
  pack:hr_document_packs!pack_id(id, title)
` as const;

// ─────────────────────────────────────────────────────────────
// Liste paginée des assignations (toutes ou par employé)
// ─────────────────────────────────────────────────────────────
export function useHrAssignments(filter?: {
  employeeId?: string;
  status?: AssignmentStatus | 'all';
  documentId?: string;
}) {
  return useQuery({
    queryKey: ['hr-assignments', 'list', filter],
    queryFn: async (): Promise<HrDocumentAssignmentWithRelations[]> => {
      let q = supabase
        .from('hr_document_assignments')
        .select(SELECT_WITH_RELATIONS)
        .order('assigned_at', { ascending: false });

      if (filter?.employeeId) q = q.eq('employee_id', filter.employeeId);
      if (filter?.documentId) q = q.eq('document_id', filter.documentId);
      if (filter?.status && filter.status !== 'all')
        q = q.eq('status', filter.status);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as HrDocumentAssignmentWithRelations[];
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Mes assignations (employé connecté)
// ─────────────────────────────────────────────────────────────
export function useMyHrAssignments() {
  const { employee } = useAuth();
  const employeeId = employee?.id ?? null;

  return useQuery({
    queryKey: ['hr-assignments', 'mine', employeeId],
    enabled: !!employeeId,
    queryFn: async (): Promise<HrDocumentAssignmentWithRelations[]> => {
      if (!employeeId) return [];
      const { data, error } = await supabase
        .from('hr_document_assignments')
        .select(SELECT_WITH_RELATIONS)
        .eq('employee_id', employeeId)
        .order('assigned_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HrDocumentAssignmentWithRelations[];
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Une assignation enrichie (vue remplir)
// ─────────────────────────────────────────────────────────────
export function useHrAssignment(assignmentId: string | null | undefined) {
  return useQuery({
    queryKey: ['hr-assignment', assignmentId],
    enabled: !!assignmentId,
    queryFn: async (): Promise<HrDocumentAssignmentWithRelations | null> => {
      if (!assignmentId) return null;
      const { data, error } = await supabase
        .from('hr_document_assignments')
        .select(SELECT_WITH_RELATIONS)
        .eq('id', assignmentId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as HrDocumentAssignmentWithRelations | null;
    },
  });
}

// ─────────────────────────────────────────────────────────────
// KPI d'une assignation (vue v_hr_assignment_progress)
// ─────────────────────────────────────────────────────────────
export function useHrAssignmentKpi(assignmentId: string | null | undefined) {
  return useQuery({
    queryKey: ['hr-assignment-kpi', assignmentId],
    enabled: !!assignmentId,
    queryFn: async (): Promise<HrAssignmentProgressKpi | null> => {
      if (!assignmentId) return null;
      const { data, error } = await supabase
        .from('v_hr_assignment_progress')
        .select('*')
        .eq('assignment_id', assignmentId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as HrAssignmentProgressKpi | null;
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Réponses d'une assignation, mappées par block_id
// ─────────────────────────────────────────────────────────────
export function useHrAssignmentResponses(
  assignmentId: string | null | undefined
) {
  return useQuery({
    queryKey: ['hr-assignment-responses', assignmentId],
    enabled: !!assignmentId,
    queryFn: async (): Promise<{
      list: HrDocumentResponse[];
      map: ResponseMap;
    }> => {
      if (!assignmentId) return { list: [], map: new Map() };
      const { data, error } = await supabase
        .from('hr_document_responses')
        .select('*')
        .eq('assignment_id', assignmentId);
      if (error) throw error;
      const list = (data ?? []) as HrDocumentResponse[];
      const map: ResponseMap = new Map();
      for (const r of list) map.set(r.block_id, r);
      return { list, map };
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Crée une assignation pour 1+ employés sur 1+ documents.
// Optionnellement liée à un pack.
// Retourne la liste des assignment_ids créés (utile pour appeler
// l'Edge Function send-document-email ensuite).
// ─────────────────────────────────────────────────────────────
interface BulkAssignInput {
  document_ids: string[];
  employee_ids: string[];
  pack_id?: string | null;
  notes?: string | null;
}

export function useBulkAssignDocuments() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: BulkAssignInput): Promise<string[]> => {
      const rows: {
        document_id: string;
        employee_id: string;
        pack_id: string | null;
        assigned_by: string | null;
        notes: string | null;
      }[] = [];
      for (const doc_id of input.document_ids) {
        for (const emp_id of input.employee_ids) {
          rows.push({
            document_id: doc_id,
            employee_id: emp_id,
            pack_id: input.pack_id ?? null,
            assigned_by: user?.id ?? null,
            notes: input.notes ?? null,
          });
        }
      }
      if (rows.length === 0) return [];

      const { data, error } = await supabase
        .from('hr_document_assignments')
        .insert(rows)
        .select('id');
      if (error) throw error;
      return ((data ?? []) as { id: string }[]).map((r) => r.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['hr-pending'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Update du status d'une assignation
// ─────────────────────────────────────────────────────────────
export function useUpdateHrAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<
        Pick<HrDocumentAssignment, 'status' | 'completed_at' | 'notes'>
      >;
    }): Promise<HrDocumentAssignment> => {
      const { data, error } = await supabase
        .from('hr_document_assignments')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as HrDocumentAssignment;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['hr-assignment', vars.id] });
      queryClient.invalidateQueries({ queryKey: ['hr-assignments'] });
      queryClient.invalidateQueries({
        queryKey: ['hr-assignment-kpi', vars.id],
      });
      queryClient.invalidateQueries({ queryKey: ['hr-pending'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Delete (cancel) une assignation
// ─────────────────────────────────────────────────────────────
export function useDeleteHrAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('hr_document_assignments')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['hr-pending'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Submit / update d'une réponse (par l'employé)
// ─────────────────────────────────────────────────────────────
interface UpsertResponseInput {
  assignment_id: string;
  block_id: string;
  value_text?: string | null;
  value_bool?: boolean | null;
  signature_data_url?: string | null;
}

export function useUpsertHrResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpsertResponseInput): Promise<HrDocumentResponse> => {
      const { data, error } = await supabase
        .from('hr_document_responses')
        .upsert(
          {
            assignment_id: input.assignment_id,
            block_id: input.block_id,
            value_text: input.value_text ?? null,
            value_bool: input.value_bool ?? null,
            signature_data_url: input.signature_data_url ?? null,
            signed_at: new Date().toISOString(),
          },
          { onConflict: 'assignment_id,block_id' }
        )
        .select()
        .single();
      if (error) throw error;

      // Si l'assignation est encore "pending", passe en "in_progress"
      await supabase
        .from('hr_document_assignments')
        .update({ status: 'in_progress' })
        .eq('id', input.assignment_id)
        .eq('status', 'pending');

      return data as HrDocumentResponse;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['hr-assignment-responses', vars.assignment_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['hr-assignment-kpi', vars.assignment_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['hr-assignment', vars.assignment_id],
      });
      queryClient.invalidateQueries({ queryKey: ['hr-pending'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Pending : mes assignations non complétées (pour la pastille rouge)
// ─────────────────────────────────────────────────────────────
export function useMyPendingHrAssignments() {
  const { employee } = useAuth();
  const employeeId = employee?.id ?? null;

  return useQuery({
    queryKey: ['hr-pending', employeeId],
    enabled: !!employeeId,
    queryFn: async (): Promise<HrDocumentAssignmentWithRelations[]> => {
      if (!employeeId) return [];
      const { data, error } = await supabase
        .from('hr_document_assignments')
        .select(SELECT_WITH_RELATIONS)
        .eq('employee_id', employeeId)
        .in('status', ['pending', 'in_progress'])
        .order('assigned_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as HrDocumentAssignmentWithRelations[];
    },
    staleTime: 30_000,
  });
}

// ─────────────────────────────────────────────────────────────
// Trigger l'Edge Function send-document-email pour les assignment_ids
// ─────────────────────────────────────────────────────────────
export function useSendHrDocumentEmail() {
  return useMutation({
    mutationFn: async (assignmentIds: string[]) => {
      if (assignmentIds.length === 0) return { sent: 0, failed: 0 };
      const { data, error } = await supabase.functions.invoke(
        'send-document-email',
        { body: { assignment_ids: assignmentIds } }
      );
      if (error) throw new Error(error.message || 'Erreur envoi email');
      if (data?.error) throw new Error(data.error);
      return data as {
        success: boolean;
        sent: number;
        failed: number;
      };
    },
  });
}
